import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getAccommodationsBlurPlaceholder } from "@/utils/accommodationsBlurPlaceholders";
import { Room } from "@/types/room";
import {
  Users,
  Square,
  Bed,
  Bath,
  ChevronLeft,
  ChevronRight,
  Mountain,
  Wifi,
  Tv,
  Wind,
  ChefHat,
  Shield,
  Star,
  Share2,
  Coffee,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useViewportAutoplay } from "@/hooks/useViewportAutoplay";

interface AlpineClassicDoubleTwinSectionProps {
  room: Room;
  onViewDetails?: (roomId: string) => void;
  onQuickBook?: (roomId: string) => void;
}

const AlpineClassicDoubleTwinSection: React.FC<
  AlpineClassicDoubleTwinSectionProps
> = ({ room, onViewDetails, onQuickBook }) => {
  const { t } = useLanguage();

  // Viewport-based auto-play hook
  const {
    currentImageIndex,
    isAutoPlaying,
    elementRef,
    handlePreviousImage,
    handleNextImage,
    handleImageClick,
  } = useViewportAutoplay({
    imageCount: room.images.length,
    intervalMs: 4000,
    threshold: 0.7,
  });

  const handleViewDetails = () => {
    onViewDetails?.(room.id);
  };

  const handleQuickBook = () => {
    onQuickBook?.(room.id);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/accommodations#alpine-classic-double-twin`;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `${room.name} - Park Hotel Beatenberg`,
          text: `Check out our Alpine Classic Double/Twin Rooms with flexible bedding and charming village views`,
          url: url,
        });
      } catch (error) {
        // Fallback to clipboard if share is cancelled
        copyToClipboard(url);
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      copyToClipboard(url);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could add a toast notification here
      console.log("Link copied to clipboard!");
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
    }
  };

  const currentImage = room.images[currentImageIndex];

  // Feature badges data
  const featureBadges = [
    {
      icon: Users,
      label: "Occupancy",
      value: t("rooms.alpineClassicDoubleTwinBadges.occupancy"),
      description: `${room.capacity.adults} adults, ${room.capacity.children} children`,
    },
    {
      icon: Square,
      label: "Room Size",
      value: t("rooms.alpineClassicDoubleTwinBadges.roomSize"),
      description: `${room.size.squareFeet} sq ft`,
    },
    {
      icon: Bed,
      label: "Bedding",
      value: t("rooms.alpineClassicDoubleTwinBadges.bedding"),
      description: "Flexible double or twin beds",
    },
    {
      icon: Bath,
      label: "Bathrooms",
      value: t("rooms.alpineClassicDoubleTwinBadges.bathrooms"),
      description: "Ensuite bathroom",
    },
  ];

  return (
    <section
      id="alpine-classic-double-twin"
      className="py-16 bg-gradient-to-b from-gray-50/50 to-white relative overflow-hidden"
      data-oid="9m3ms8:"
    >
      {/* Background Quadrilaterals - Mirrored positions */}
      <div
        className="absolute top-0 left-0 w-96 h-96 transform rotate-45 -translate-x-48 -translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="g-e5eet"
      ></div>
      <div
        className="absolute bottom-0 right-0 w-96 h-96 transform rotate-45 translate-x-48 translate-y-48 bg-alpine-gold/5 rounded-lg"
        data-oid="p6d58tu"
      ></div>
      <div
        className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10"
        data-oid="iew3990"
      >
        {/* Mobile Header - Shown only on mobile */}
        <div className="mb-8 lg:hidden" data-oid="2nme41_">
          <div className="mb-4" data-oid="lamlkqi">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-playfair text-gray-900"
              data-oid="xz:0iw."
            >
              {t("rooms.alpineClassicDoubleTwin")}
            </h2>
          </div>

          {/* Key badges below title - Mobile */}
          <div
            className="flex items-center gap-3 sm:gap-4 md:gap-6 flex-wrap"
            data-oid="e.cfdwv"
          >
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="ve:ptml"
            >
              <Users
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="0y2lci8"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="mzkm.fs"
              >
                {featureBadges[0].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="1-shpnp"
            >
              <Square
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="tgrmus9"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="a8y_lhi"
              >
                {featureBadges[1].value}
              </span>
            </div>
            <div
              className="flex items-center gap-1.5 sm:gap-2"
              data-oid="zhp5ceo"
            >
              <Bed
                className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0"
                data-oid="y0v.:2t"
              />

              <span
                className="text-xs sm:text-sm text-gray-900"
                data-oid="y__9-01"
              >
                {featureBadges[2].value}
              </span>
            </div>
          </div>
        </div>

        {/* 2-Column Layout: Content Left (40-50%), Carousel Right (50-60%) - MIRRORED */}
        <div
          className="grid grid-cols-1 lg:grid-cols-[50%_50%] xl:grid-cols-[45%_55%] 2xl:grid-cols-[40%_60%] gap-6 lg:gap-8 xl:gap-12 mb-12"
          data-oid="g55h03t"
        >
          {/* Left Column - Content */}
          <div
            className="flex flex-col gap-8 min-w-0 overflow-hidden pl-4 lg:pl-6 xl:pl-8 order-2 lg:order-1"
            data-oid="irr:gz2"
          >
            {/* Desktop Header - Shown only on desktop */}
            <div className="hidden lg:block" data-oid="hdu8f-h">
              <div className="mb-4" data-oid="7ecb4dr">
                <h2
                  className="text-3xl xl:text-4xl 2xl:text-5xl font-playfair text-gray-900"
                  data-oid="mvu42my"
                >
                  {t("rooms.alpineClassicDoubleTwin")}
                </h2>
              </div>

              {/* Key badges below title - Desktop */}
              <div
                className="flex items-center gap-2 lg:gap-3 xl:gap-4 2xl:gap-6 flex-wrap"
                data-oid="s.ev0s3"
              >
                <div className="flex items-center gap-2" data-oid="p0uaw_t">
                  <Users
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="x.pi.2p"
                  />

                  <span className="text-sm text-gray-900" data-oid=":nbzbiz">
                    {featureBadges[0].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="vohp3f3">
                  <Square
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="6xs_t9_"
                  />

                  <span className="text-sm text-gray-900" data-oid="tkrsy8j">
                    {featureBadges[1].value}
                  </span>
                </div>
                <div className="flex items-center gap-2" data-oid="8.8wi3m">
                  <Bed
                    className="w-5 h-5 text-gray-600 flex-shrink-0"
                    data-oid="pjrjrfx"
                  />

                  <span className="text-sm text-gray-900" data-oid="h6gzmd6">
                    {featureBadges[2].value}
                  </span>
                </div>
              </div>
            </div>

            {/* 1. Description */}
            <div className="overflow-hidden" data-oid="yevyctk">
              <p
                className="text-sm lg:text-base xl:text-lg text-gray-700 leading-relaxed break-words"
                data-oid=".3mzp:_"
              >
                <span className="sm:hidden" data-oid="rltagx7">
                  {t("rooms.alpineClassicDoubleTwinDescription.mobile")}
                </span>
                <span className="hidden sm:inline" data-oid="b:483_q">
                  {t("rooms.alpineClassicDoubleTwinDescription.desktop")}
                </span>
              </p>
            </div>

            {/* 2. All Features & Amenities - Consolidated */}
            <div className="grid grid-cols-1 gap-y-4" data-oid="dm9bwys">
              {/* Flexible bedding configuration */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="llll9e1"
              >
                <Bed
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="a2qlnzt"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="0-_2yql"
                >
                  {t("rooms.alpineClassicDoubleTwinFeatures.flexibleBedding")}
                </span>
              </div>

              {/* Charming village views */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="8hx7hn1"
              >
                <Mountain
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="-skyrsc"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="fuf7uzm"
                >
                  {t("rooms.alpineClassicDoubleTwinFeatures.villageViews")}
                </span>
              </div>

              {/* Free Wifi */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="yz-cg7x"
              >
                <Wifi
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="dc7_at_"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="w4m05xg"
                >
                  Free Wifi
                </span>
              </div>

              {/* Premium organic bio cotton bedding */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="p3p2797"
              >
                <Star
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="bgfjwum"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="zprbkm6"
                >
                  Premium organic bio cotton bedding
                </span>
              </div>

              {/* Ensuite coffee machine */}
              <div
                className="flex items-center gap-2 lg:gap-3"
                data-oid="psns1c-"
              >
                <Coffee
                  className="w-5 h-5 text-gray-600 flex-shrink-0"
                  data-oid="tq0z2-3"
                />

                <span
                  className="text-sm text-gray-900 break-words"
                  data-oid="ie.1.r9"
                >
                  Ensuite coffee machine
                </span>
              </div>
            </div>

            {/* 3. Book Now and Share Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              data-oid="xegaxr1"
            >
              <Button
                asChild
                size="lg"
                className="bg-alpine-gold hover:bg-alpine-gold/90 text-white px-8 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                trackingType="booking"
                trackingLabel={t("nav.bookNow")}
                trackingLocation="Alpine Classic Double Twin Section"
                data-oid="xsm51qu"
              >
                <a
                  href={import.meta.env.VITE_BOOKING_URL}
                  data-oid="ldxu-yh"
                >
                  {t("nav.bookNow")}
                </a>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="bg-white/80 hover:bg-alpine-gold hover:text-white border-gray-300 hover:border-alpine-gold transition-all duration-300 px-6 py-3 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105"
                onClick={handleShare}
                aria-label="Share Alpine Classic Double/Twin Rooms"
                data-oid="1yn4qej"
              >
                <Share2 className="w-4 h-4 mr-2" data-oid="16c:1q." />
                {t("common.share")}
              </Button>
            </div>
          </div>

          {/* Right Column - Image Carousel */}
          <div
            ref={elementRef}
            className="relative w-full aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl order-1 lg:order-2"
            data-oid="19f8p2g"
          >
            <div
              className="flex h-full transition-transform duration-700 ease-in-out"
              style={{ transform: `translateX(-${currentImageIndex * 100}%)` }}
              data-oid=".n.czvw"
            >
              {room.images.map((image, index) => (
                <div
                  key={image.id}
                  className="w-full h-full flex-shrink-0"
                  data-oid=".1vhj_0"
                >
                  <OptimizedImage
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                    priority={false}
                    rootMargin="200px"
                    blurDataURL={getAccommodationsBlurPlaceholder(image.url)}
                    responsive={true}
                    data-oid="98wr60s"
                  />
                </div>
              ))}
            </div>

            {/* Image Navigation */}
            {room.images.length > 1 && (
              <>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handlePreviousImage}
                  aria-label="Previous image"
                  data-oid="wgu7zzp"
                >
                  <ChevronLeft className="w-5 h-5" data-oid="usmwyoj" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm shadow-lg border-gray-300 hover:scale-110 transition-all duration-300"
                  onClick={handleNextImage}
                  aria-label="Next image"
                  data-oid="nl4_uzm"
                >
                  <ChevronRight className="w-5 h-5" data-oid="as_ajw7" />
                </Button>
              </>
            )}

            {/* Image Indicators */}
            {room.images.length > 1 && (
              <div
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2"
                data-oid="g.btk0e"
              >
                {room.images.map((_, index) => (
                  <button
                    key={index}
                    className={cn(
                      "w-2 h-2 rounded-full transition-all duration-300",
                      index === currentImageIndex
                        ? "bg-white scale-125"
                        : "bg-white/50 hover:bg-white/75",
                    )}
                    onClick={() => handleImageClick(index)}
                    aria-label={`Go to image ${index + 1}`}
                    data-oid="iy4e9tl"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AlpineClassicDoubleTwinSection;
